/*
 * Copyright 2020 American Express Travel Related Services Company, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
*/

import  React, { useEffect } from 'react';
import dayjs from 'dayjs';
import updateLocale  from 'dayjs/plugin/updateLocale';
import isBetween from 'dayjs/plugin/isBetween';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import isYesterday from 'dayjs/plugin/isYesterday';
import hoistStatics from 'hoist-non-react-statics';
import markets from './markets';
import esMonths from './esMonths';

interface IProps {
  locale: string;
}

dayjs.extend(updateLocale);
dayjs.extend(isBetween);
dayjs.extend(isLeapYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(isYesterday);

export const withDayJSLocale = (Component: React.ComponentType<IProps>) => {
  const WithDayJSLocaleFC = (props: IProps) => {
    useEffect(() => {
      const { locale } = props;

      const marketName: string = markets[locale];

      importDayJsLocale(marketName).then(() => {
        dayjs.locale(marketName);
        
        if (locale.includes('es')) {
          dayjs.updateLocale(marketName, {
            months: esMonths
          })
        };
      });
    }, [props.locale]);

    const importDayJsLocale = (marketName: string) => import(`dayjs/locale/${marketName}`);
    
    return <Component {...props} />;
  }
  
  hoistStatics(WithDayJSLocaleFC, Component);
  return WithDayJSLocaleFC;
};
